import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TinyCard, CardHoverPreview } from "@/components/Card3D";
import { SelectedCardBar } from "@/components/SelectedCardBar";
import { Skull, Users, Search, ChevronLeft, ChevronRight, AlertTriangle, Check, DoorOpen, Gem, Swords, Shield, Heart, Wind, Mountain, ArrowRight, Dices, Zap, Map as MapIcon } from "lucide-react";
import { AttributeChips, traitColor } from "@/components/ArenaBits";
import { BareGearWarning, isBareOfGear } from "@/components/BareGearWarning";
import { ClosedLoopNotice } from "@/components/ClosedLoopNotice";
import { toast } from "sonner";

const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const SORTS = { atk: "Attack", def: "Defense", hp: "Health", spd: "Speed", title: "Name" };
const PAGE = 12;

function StatPip({ icon: Icon, label, value, color }) {
  return <span className="inline-flex items-center gap-1 font-mono text-[10px]" style={{ color }}><Icon className="h-3 w-3" /> {label} {value}</span>;
}

// Ticks down smoothly every second locally instead of only jumping whenever
// the 4s list poll happens to land, so "Xs open" doesn't skip by irregular
// amounts (4s, 8s, 12s...) between refreshes.
function LiveCountdown({ seconds }) {
  const [local, setLocal] = useState(seconds ?? null);
  const lastRef = useRef(seconds);
  useEffect(() => {
    if (seconds !== lastRef.current) {
      lastRef.current = seconds;
      setLocal(seconds ?? null);
    }
  }, [seconds]);
  useEffect(() => {
    if (local == null || local <= 0) return;
    const t = setTimeout(() => setLocal((s) => (s == null ? null : Math.max(0, s - 1))), 1000);
    return () => clearTimeout(t);
  }, [local]);
  return local;
}

function EditionPicker({ group, selected, onPick, onClose }) {
  if (!group) return null;
  const sorted = [...group.cards].sort((a, b) => (a.edition_number || 0) - (b.edition_number || 0));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md max-h-[85vh] overflow-y-auto" data-testid="cavern-edition-picker">
        <DialogHeader><DialogTitle className="font-display text-2xl text-white tracking-[0.06em]">{group.rep.title}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Pick which edition of this card to send into the Cavern delve.</DialogDescription>
        <p className="text-[11px] text-white/50 -mt-1 mb-1">{sorted.length} editions owned — pick your delver.</p>
        <div className="space-y-2">
          {sorted.map((c) => {
            const a = c.arena || {}; const isSel = c.id === selected;
            return (
              <CardHoverPreview key={c.id} card={c} side="right">
                <div role="button" tabIndex={0} data-testid={`cavern-edition-${c.id}`}
                  onClick={() => onPick(c.id)} onKeyDown={(e) => { if (e.key === "Enter") onPick(c.id); }}
                  className={`hud-pill rounded-xl p-3 flex items-center justify-between gap-3 cursor-pointer transition-colors duration-150 outline-none ${isSel ? "border-violet-300 bg-violet-300/10" : "hover:bg-white/5 hover:border-violet-400/30"}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-white">Edition #{c.edition_number ?? "—"}</div>
                    <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 mt-1">
                      <StatPip icon={Swords} label="ATK" value={a.atk ?? "—"} color="#fbbf24" />
                      <StatPip icon={Shield} label="DEF" value={a.def ?? "—"} color="#a3e635" />
                      <StatPip icon={Heart} label="HP" value={a.hp ?? "—"} color="#f43f5e" />
                      <StatPip icon={Wind} label="SPD" value={a.spd ?? "—"} color="#22d3ee" />
                    </div>
                  </div>
                  <Button size="sm" className={isSel ? "bg-emerald-400 text-black hover:bg-emerald-300 shrink-0" : "bg-violet-300 text-black hover:bg-violet-200 shrink-0"}>
                    {isSel ? <><Check className="h-3.5 w-3.5 mr-1" /> Selected</> : "Send this"}
                  </Button>
                </div>
              </CardHoverPreview>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CavernPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState(null);
  const [editionPick, setEditionPick] = useState(null);
  const [mode, setMode] = useState("coop"); // "coop" | "dungeon"
  const [rooms, setRooms] = useState(4);
  const [dungeonRoll, setDungeonRoll] = useState(null);
  const [dungeonPlan, setDungeonPlan] = useState([]);
  const [dungeonXp, setDungeonXp] = useState(0);
  const [loadingDungeon, setLoadingDungeon] = useState(false);
  const [foe, setFoe] = useState("guard");
  const [depth, setDepth] = useState(null);
  const [monsters, setMonsters] = useState([]);
  const [rollId, setRollId] = useState(null);
  const [choiceIdx, setChoiceIdx] = useState(null);
  const [monsterImage, setMonsterImage] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [slots, setSlots] = useState(3);
  const [search, setSearch] = useState("");
  const [rarity, setRarity] = useState("all");
  const [sort, setSort] = useState("atk");
  const [page, setPage] = useState(0);

  const { data: foes } = useQuery({ queryKey: ["cavern-foes"], queryFn: async () => (await api.get("/cavern/foes")).data });
  const { data: cards } = useQuery({ queryKey: ["my-cards"], queryFn: async () => (await api.get("/cards/mine")).data });
  const { data: openDelves } = useQuery({ queryKey: ["cavern-open"], queryFn: async () => (await api.get("/cavern/open")).data, refetchInterval: 4000 });
  const { data: history } = useQuery({ queryKey: ["cavern-history"], queryFn: async () => (await api.get("/cavern")).data });

  const groups = useMemo(() => {
    let list = [...(cards || [])];
    if (search.trim()) list = list.filter((c) => (c.title || "").toLowerCase().includes(search.toLowerCase()));
    if (rarity !== "all") list = list.filter((c) => c.rarity === rarity);
    const map = new Map();
    for (const c of list) {
      const key = c.template_id || c.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    const gs = [...map.values()].map((cs) => ({ key: cs[0].template_id || cs[0].id, cards: cs, rep: cs[0] }));
    gs.sort((a, b) => sort === "title" ? (a.rep.title || "").localeCompare(b.rep.title || "") : ((b.rep.arena?.[sort] ?? 0) - (a.rep.arena?.[sort] ?? 0)));
    return gs;
  }, [cards, search, rarity, sort]);

  const pageCount = Math.max(1, Math.ceil(groups.length / PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const shown = groups.slice(safePage * PAGE, safePage * PAGE + PAGE);

  useEffect(() => {
    const pre = searchParams.get("preselect");
    if (pre && cards && cards.some((c) => c.id === pre)) {
      setSelected(pre);
      toast.success("Delver pre-selected from your collection");
      const next = new URLSearchParams(searchParams);
      next.delete("preselect");
      setSearchParams(next, { replace: true });
    }
  }, [cards, searchParams, setSearchParams]);

  const create = useMutation({
    mutationFn: async () => {
      if (mode === "dungeon") {
        return (await api.post("/cavern/dungeon/create", { card_id: selected, rooms, roll_id: dungeonRoll, slots })).data;
      }
      const chosen = monsters.find((m) => m.candidate_index === choiceIdx);
      return (await api.post("/cavern/create", { card_id: selected, depth, roll_id: rollId, choice_index: choiceIdx, monster_image: chosen?.image || null, slots })).data;
    },
    onSuccess: (d) => navigate(`/cavern/${d.id}`),
    onError: (e) => toast.error(apiError(e, "Couldn't open the delve")),
  });
  // Cache the free first roll per depth / room-count so toggling selections can't
  // be abused as a free re-roll — fresh monsters only come from the paid Re-roll (1C).
  const depthRollsRef = useRef({});
  const dungeonRollsRef = useRef({});
  const pickDepth = async (dp, reroll = false) => {
    const cached = depthRollsRef.current[dp];
    if (!reroll && cached) {
      setDepth(dp); setChoiceIdx(null); setRollId(cached.rollId); setMonsters(cached.monsters);
      return;
    }
    setDepth(dp); setChoiceIdx(null); setMonsters([]); setRollId(null); setRolling(true);
    try {
      const r = await api.get(`/cavern/options?depth=${dp}${reroll ? "&reroll=true" : ""}`);
      depthRollsRef.current[dp] = { rollId: r.data.roll_id, monsters: r.data.monsters };
      setRollId(r.data.roll_id); setMonsters(r.data.monsters);
      if (reroll) toast.success("Re-rolled — 1 credit spent");
    } catch (e) {
      toast.error(apiError(e, "Couldn't summon monsters"));
      if (reroll && cached) { setRollId(cached.rollId); setMonsters(cached.monsters); }
    }
    finally { setRolling(false); }
  };
  const rollDungeon = async (n, reroll = false) => {
    const cached = dungeonRollsRef.current[n];
    if (!reroll && cached) {
      setRooms(n); setDungeonRoll(cached.rollId); setDungeonPlan(cached.plan); setDungeonXp(cached.xp);
      return;
    }
    setRooms(n); setDungeonRoll(null); setDungeonPlan([]); setLoadingDungeon(true);
    try {
      const r = await api.get(`/cavern/dungeon/options?rooms=${n}${reroll ? "&reroll=true" : ""}`);
      dungeonRollsRef.current[n] = { rollId: r.data.roll_id, plan: r.data.monsters, xp: r.data.total_win_xp };
      setDungeonRoll(r.data.roll_id); setDungeonPlan(r.data.monsters); setDungeonXp(r.data.total_win_xp);
      if (reroll) toast.success("Re-rolled — 1 credit spent");
    } catch (e) {
      toast.error(apiError(e, "Couldn't map the dungeon"));
      if (reroll && cached) { setDungeonRoll(cached.rollId); setDungeonPlan(cached.plan); setDungeonXp(cached.xp); }
    }
    finally { setLoadingDungeon(false); }
  };
  const join = useMutation({
    mutationFn: async (delveId) => (await api.post(`/cavern/${delveId}/join`, { card_id: selected })).data,
    onSuccess: (d) => navigate(`/cavern/${d.id}`),
    onError: (e) => toast.error(apiError(e, "Couldn't join")),
  });

  const pickGroup = (g) => { if (g.cards.length > 1) setEditionPick(g); else setSelected(g.rep.id); };
  const foeList = foes?.foes || [];
  const selectedCard = useMemo(() => (cards || []).find((c) => c.id === selected), [cards, selected]);
  const bareNames = selectedCard && isBareOfGear(selectedCard) ? [selectedCard.title || "Your delver"] : [];

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
      <div className="flex items-center gap-2.5 sm:gap-3 mb-1">
        <div className="h-9 w-9 sm:h-11 sm:w-11 rounded-lg sm:rounded-xl bg-violet-400/15 border border-violet-300/30 flex items-center justify-center shrink-0"><Mountain className="h-5 w-5 sm:h-6 sm:w-6 text-violet-300" /></div>
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl text-white tracking-[0.04em] leading-[1.05]">THE CAVERN</h1>
          <p className="text-white/50 text-[12.5px] sm:text-sm leading-snug mt-0.5">A co-op delve. Form a party, descend together, and face one shared monster. <span className="text-violet-300">Empty seats fill with AI allies.</span></p>
        </div>
      </div>

      <ClosedLoopNotice className="mt-3 sm:mt-4" testid="cavern-closed-loop" />

      <div data-testid="cavern-stakes-warning" className="mt-4 sm:mt-5 rounded-xl sm:rounded-2xl border border-violet-400/40 bg-gradient-to-r from-red-500/[0.12] via-violet-400/[0.10] to-emerald-500/[0.12] p-3 sm:p-4 flex items-start gap-2.5 sm:gap-3">
        <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-violet-300 shrink-0 mt-0.5" />
        <div className="text-[12.5px] sm:text-sm leading-snug sm:leading-relaxed">
          <span className="font-display tracking-[0.06em] text-white text-sm sm:text-base">REAL CONSEQUENCES — descend at your own risk.</span>
          <div className="mt-1 text-white/80">
            <span data-testid="cavern-win-reward-rule"><span className="text-emerald-300 font-semibold">Slay the monster</span> and survive → <span className="text-emerald-300 font-semibold">25% card-mint chance</span>; otherwise credits and a Reroll Token. Every surviving winner has a 60% gear chance, plus depth rewards.</span>
            {" "}<span className="text-red-300 font-semibold">Fall in battle</span> or the party wipes → your card is <span className="text-red-300 font-semibold underline decoration-red-400/60">forfeited to the platform</span>.
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5 sm:gap-8 mt-5 sm:mt-8">
        {/* Left: foe select + delver picker */}
        <div>
          <div className="flex gap-2 mb-5" data-testid="cavern-mode-tabs">
            <button data-testid="cavern-mode-coop" onClick={() => setMode("coop")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 border transition-all ${mode === "coop" ? "border-violet-300 bg-violet-300/10 text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:bg-white/[0.05]"}`}>
              <Users className="h-4 w-4 text-violet-300" />
              <div className="text-left leading-tight">
                <div className="font-display tracking-[0.04em] text-sm">CO-OP RAID</div>
                <div className="font-mono text-[9px] text-white/40">One shared monster</div>
              </div>
            </button>
            <button data-testid="cavern-mode-dungeon" onClick={() => setMode("dungeon")}
              className={`flex items-center gap-2 rounded-xl px-4 py-2.5 border transition-all ${mode === "dungeon" ? "border-amber-300 bg-amber-300/10 text-white" : "border-white/10 bg-white/[0.02] text-white/55 hover:bg-white/[0.05]"}`}>
              <MapIcon className="h-4 w-4 text-amber-300" />
              <div className="text-left leading-tight">
                <div className="font-display tracking-[0.04em] text-sm">DUNGEON</div>
                <div className="font-mono text-[9px] text-white/40">Straight-through 2–6 rooms</div>
              </div>
            </button>
          </div>

          {mode === "coop" && (<>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3">1 · Choose the depth</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {foeList.map((f) => {
              const isSel = depth === f.depth;
              return (
                <button key={f.key} data-testid={`cavern-depth-${f.depth}`} onClick={() => pickDepth(f.depth)}
                  className={`text-left rounded-2xl p-3.5 border transition-all duration-200 ${isSel ? "scale-[1.02]" : "hover:bg-white/[0.04]"}`}
                  style={{ borderColor: isSel ? f.color : "rgba(255,255,255,0.08)", background: isSel ? `${f.color}14` : "rgba(255,255,255,0.02)", boxShadow: isSel ? `0 0 20px ${f.color}33` : "none" }}>
                  <div className="flex items-center justify-between">
                    <Skull className="h-5 w-5" style={{ color: f.color }} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Depth {f.depth}</span>
                  </div>
                  <div className="mt-2 font-display tracking-[0.04em] text-white text-base leading-tight">Depth {f.depth}</div>
                  <div className="mt-1 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider" style={{ color: f.color }}>
                    <Gem className="h-3 w-3" /> {f.reward} drop
                  </div>
                </button>
              );
            })}
          </div>

          {depth != null && (
            <div className="mb-7">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45">2 · Pick your monster</h2>
                <button data-testid="cavern-reroll-monsters" onClick={() => pickDepth(depth, true)} disabled={rolling} title="Costs 1 credit" className="text-[11px] font-mono text-violet-300 hover:text-violet-200 disabled:opacity-40 flex items-center gap-1"><Dices className="h-3.5 w-3.5" /> Re-roll · 1C</button>
              </div>
              {rolling ? (
                <div className="text-white/40 text-sm py-6 text-center">Summoning beasts…</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {monsters.map((m) => {
                    const isSel = choiceIdx === m.candidate_index;
                    return (
                      <button key={m.candidate_index} data-testid={`cavern-monster-${m.candidate_index}`} onClick={() => setChoiceIdx(m.candidate_index)}
                        className={`text-left rounded-2xl p-3.5 border transition-all ${isSel ? "scale-[1.01]" : "hover:bg-white/[0.04]"}`}
                        style={{ borderColor: isSel ? m.color : "rgba(255,255,255,0.08)", background: isSel ? `${m.color}14` : "rgba(255,255,255,0.02)", boxShadow: isSel ? `0 0 18px ${m.color}33` : "none" }}>
                        <div className="flex items-center gap-3">
                          {m.image ? (
                            <img src={m.image} alt={m.name} className="h-12 w-12 rounded-lg object-cover border shrink-0" style={{ borderColor: `${m.color}66` }} data-testid={`cavern-monster-img-${m.candidate_index}`} />
                          ) : (
                            <div className="h-12 w-12 rounded-lg bg-white/5 border border-white/10 grid place-items-center shrink-0"><Skull className="h-5 w-5" style={{ color: m.color }} /></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="font-display text-white text-sm leading-tight">{m.name}</span>
                            {m.win_xp != null && (
                              <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.5" data-testid={`cavern-monster-xp-${m.candidate_index}`} title="XP each surviving delver earns on a win — scales with the colour (strength) of this monster's traits">
                                <Zap className="h-3 w-3 text-amber-300" />
                                <span className="font-mono text-[11px] text-amber-200">+{m.win_xp} XP / delver on win</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2.5 space-y-1.5">
                          {(m.traits || []).map((t) => (
                            <div key={t.name} className="text-[11px] leading-snug" data-testid={`cavern-monster-trait-${m.candidate_index}`}>
                              <span style={traitColor(t)} className="font-mono font-medium">✦ {t.name}</span>
                              <span className="text-white/45"> — {t.desc}</span>
                            </div>
                          ))}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          </>)}

          {mode === "dungeon" && (
            <div className="mb-7">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3">1 · How many rooms?</h2>
              <div className="grid grid-cols-5 gap-2 mb-2">
                {[2, 3, 4, 5, 6].map((n) => (
                  <button key={n} data-testid={`dungeon-rooms-${n}`} onClick={() => rollDungeon(n)}
                    className={`py-2.5 rounded-xl font-mono text-sm border transition-all ${rooms === n && dungeonRoll ? "border-amber-300 bg-amber-300/15 text-white" : "border-white/10 bg-white/[0.02] text-white/60 hover:bg-white/[0.05]"}`}>
                    {n} <span className="text-[10px] text-white/40">rms</span>
                  </button>
                ))}
              </div>
              <p data-testid="cavern-dungeon-reward-rule" className="text-[11px] text-white/40 mb-3">Each room is tougher, and the <span className="text-amber-300">final room jumps +2 tiers</span>. HP carries between rooms; downed delvers stay down. Survive the last room for loot and a 25% card-mint chance.</p>

              {loadingDungeon ? (
                <div className="text-white/40 text-sm py-6 text-center">Mapping the dungeon…</div>
              ) : dungeonPlan.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45">2 · The descent</h2>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 border border-amber-400/30 px-2 py-0.5" data-testid="dungeon-total-xp" title="Total XP a delver can bank by clearing every room">
                        <Zap className="h-3 w-3 text-amber-300" /><span className="font-mono text-[11px] text-amber-200">{dungeonXp} XP full clear</span>
                      </span>
                      <button data-testid="dungeon-reroll" onClick={() => rollDungeon(rooms, true)} disabled={loadingDungeon} title="Costs 1 credit" className="text-[11px] font-mono text-amber-300 hover:text-amber-200 disabled:opacity-40 flex items-center gap-1"><Dices className="h-3.5 w-3.5" /> Re-roll · 1C</button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {dungeonPlan.map((r, i) => {
                      const isFinal = i === dungeonPlan.length - 1;
                      return (
                        <div key={r.room} data-testid={`dungeon-room-${i}`} className="rounded-xl p-3 border flex items-start gap-3"
                          style={{ borderColor: isFinal ? `${r.color}88` : "rgba(255,255,255,0.08)", background: isFinal ? `${r.color}10` : "rgba(255,255,255,0.02)" }}>
                          <div className="flex flex-col items-center shrink-0 pt-0.5">
                            <span className="font-display text-lg leading-none" style={{ color: r.color }}>{i + 1}</span>
                            <span className="font-mono text-[8px] uppercase tracking-wider text-white/35">room</span>
                          </div>
                          {r.image ? (
                            <img src={r.image} alt={r.name} className="h-11 w-11 rounded-lg object-cover border shrink-0" style={{ borderColor: `${r.color}66` }} />
                          ) : (
                            <div className="h-11 w-11 rounded-lg bg-white/5 border border-white/10 grid place-items-center shrink-0"><Skull className="h-5 w-5" style={{ color: r.color }} /></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-display text-white text-sm leading-tight">{r.name}</span>
                              {isFinal && <span className="font-mono text-[8px] uppercase tracking-wider rounded px-1.5 py-0.5" style={{ background: `${r.color}22`, color: r.color }}>Final · tier {r.tier}</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: r.color }}><Gem className="h-3 w-3 inline mr-0.5" />{r.reward}</span>
                              <span className="inline-flex items-center gap-0.5 font-mono text-[10px] text-amber-200" data-testid={`dungeon-room-xp-${i}`}><Zap className="h-3 w-3" />+{r.win_xp} XP</span>
                            </div>
                            {(r.traits || []).length > 0 && (
                              <div className="mt-1.5 space-y-1">
                                {r.traits.map((t) => (
                                  <div key={t.name} className="text-[10px] leading-snug flex items-start gap-1.5" data-testid={`dungeon-room-${i}-trait-${t.name}`}>
                                    <span className="font-mono font-semibold shrink-0" style={traitColor(t)}>✦ {t.name}</span>
                                    {t.desc && <span className="text-white/55">— {t.desc}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="hud-pill rounded-2xl p-8 text-center text-white/45 text-sm">Pick a room count to map your dungeon.</div>
              )}
            </div>
          )}

          {selected ? (
            <SelectedCardBar card={(cards || []).find((c) => c.id === selected)} onClear={() => setSelected(null)} testid="cavern-selected" />
          ) : (
          <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mr-auto">Choose your delver</h2>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
              <Input data-testid="cavern-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Search…" className="h-8 w-36 pl-7 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs" />
            </div>
            <Select value={rarity} onValueChange={(v) => { setRarity(v); setPage(0); }}>
              <SelectTrigger data-testid="cavern-rarity-filter" className="h-8 w-28 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{RARITIES.map((r) => <SelectItem key={r} value={r} className="text-xs capitalize">{r}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="cavern-sort" className="h-8 w-28 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(222_22%_8%)] border-white/10 text-white">{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {groups.length === 0 ? (
            <div className="hud-pill rounded-2xl p-10 text-center text-white/45">No cards match. Grab some from the Store first.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {shown.map((g) => {
                  const sel = g.cards.find((c) => c.id === selected);
                  const statCard = sel || g.rep; const a = statCard.arena || {}; const isSel = !!sel;
                  return (
                    <div key={g.key} data-testid={`cavern-pick-${g.rep.id}`}
                      className={`rounded-xl transition-transform ${isSel ? "ring-2 ring-violet-300 ring-offset-2 ring-offset-[hsl(220_22%_6%)] scale-[1.02]" : ""}`}>
                      <TinyCard card={statCard} count={g.cards.length} idKey={g.key} previewSide="top" onClick={() => pickGroup(g)}
                        footer={
                          <div className="mt-1 px-0.5">
                            <div className="text-white text-[11px] font-medium truncate flex items-center gap-1">{isSel && <Check className="h-3 w-3 text-violet-300 shrink-0" />}{statCard.title}</div>
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                              <StatPip icon={Swords} label="ATK" value={a.atk ?? "—"} color="#fbbf24" />
                              <StatPip icon={Heart} label="HP" value={a.hp ?? "—"} color="#f43f5e" />
                            </div>
                          </div>
                        } />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-mono text-[10px] text-white/35">{groups.length} card{groups.length === 1 ? "" : "s"} · page {safePage + 1}/{pageCount}</span>
                <div className="flex gap-2">
                  <Button data-testid="cavern-prev-page" size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="border-white/15 text-white/70 h-7"><ChevronLeft className="h-4 w-4" /></Button>
                  <Button data-testid="cavern-next-page" size="sm" variant="outline" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)} className="border-white/15 text-white/70 h-7"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
          </>
          )}
        </div>

        {/* Right: host / join / history */}
        <div className="space-y-4">
          <div className="hud-pill rounded-2xl p-5">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Host a delve</h3>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">Party size</div>
            <div className="grid grid-cols-4 gap-1.5">
              {[2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`cavern-slots-${n}`} onClick={() => setSlots(n)} className={`py-2 rounded-lg font-mono text-sm transition-colors ${slots === n ? "bg-violet-300 text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>{n}</button>
              ))}
            </div>
            <p className="text-[11px] text-white/40 mt-2">{slots} delvers. Empty seats fill with power-matched AI allies when you begin.</p>
            <BareGearWarning names={bareNames} context={mode === "dungeon" ? "the Dungeon" : "the Cavern"} className="mt-3" />
            <Button data-testid="cavern-create-button" disabled={!selected || (mode === "dungeon" ? dungeonRoll == null : (depth == null || rollId == null || choiceIdx == null)) || create.isPending} onClick={() => create.mutate()} className="w-full mt-3 bg-violet-300 text-black hover:bg-violet-200 disabled:opacity-40 font-medium">
              <ArrowRight className="h-4 w-4 mr-1.5" /> {create.isPending ? "Opening delve…" : (mode === "dungeon" ? "Open Dungeon" : "Open Delve")}
            </Button>
            {!selected && <p className="text-[11px] text-white/35 mt-2 text-center">Pick a delver first.</p>}
            {selected && mode === "dungeon" && dungeonRoll == null && <p className="text-[11px] text-white/35 mt-2 text-center">Map a dungeon first.</p>}
          </div>

          <div className="hud-pill rounded-2xl p-5">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3 flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5" /> Join a party</h3>
            {(openDelves || []).length > 0 && !selected && <p className="text-[11px] text-violet-300/80 mb-2" data-testid="cavern-join-hint">↞ Pick a delver on the left first, then Join.</p>}
            {(openDelves || []).length > 0 && <BareGearWarning names={bareNames} context="the Cavern" className="mb-2" />}
            {(openDelves || []).length === 0 ? (
              <p className="text-[12px] text-white/40">No open delves right now. Host one!</p>
            ) : (
              <div className="space-y-2">
                {openDelves.map((d) => (
                  <div key={d.id} data-testid={`open-delve-${d.id}`} className="rounded-lg bg-white/[0.03] px-2 py-1.5">
                    <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {d.party.slice(0, 4).map((p) => (
                        <CardHoverPreview key={p.idx} card={p.card} side="top">
                          <div
                            data-testid={`open-delve-${d.id}-party-${p.idx}`}
                            className={`h-7 w-5 rounded overflow-hidden bg-black border border-black rarity-${p.rarity} cursor-help hover:z-10 hover:scale-125 transition-transform`}
                          >
                            {p.card?.front_image && <img src={p.card.front_image} alt="" className="w-full h-full object-cover" />}
                          </div>
                        </CardHoverPreview>
                      ))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-white text-xs flex items-center gap-1.5 cursor-help"
                        title={`${d.foe.name}${d.foe.rarity ? " · " + d.foe.rarity : ""}${d.foe.blurb ? "\n" + d.foe.blurb : ""}${d.foe.win_xp ? "\nWin XP: " + d.foe.win_xp : ""}${d.foe.special ? "\nSpecial: " + d.foe.special : ""}`}
                      >
                        {d.mode === "dungeon" ? <MapIcon className="h-3 w-3 text-amber-300" /> : <Skull className="h-3 w-3" style={{ color: d.foe.color }} />} {d.foe.name}
                      </div>
                      <div className="font-mono text-[10px] text-white/45">
                        {d.mode === "dungeon" && d.dungeon ? <span className="text-amber-300">Dungeon · {d.dungeon.total_rooms} rooms · </span> : null}
                        {d.party.length}/{d.slots} · <LiveCountdown seconds={d.seconds_open_left} />s open
                      </div>
                    </div>
                    {d.joined ? (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/cavern/${d.id}`)} className="text-violet-300 h-7">Open</Button>
                    ) : (
                      <Button size="sm" data-testid={`join-delve-${d.id}`} disabled={join.isPending} onClick={() => selected ? join.mutate(d.id) : toast.error("Pick a delver on the left first")} className="bg-violet-400 text-black hover:bg-violet-300 h-7 disabled:opacity-40">Join</Button>
                    )}
                    </div>
                    {d.mode === "dungeon" && d.dungeon?.rooms?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/5 space-y-1.5" data-testid={`open-delve-${d.id}-rooms`}>
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-300/70">Room plan</div>
                        {d.dungeon.rooms.map((r, i) => (
                          <div key={r.room} className="rounded-md bg-black/25 border border-white/5 px-1.5 py-1" data-testid={`open-delve-${d.id}-room-${i}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] text-white/80 truncate">
                                <span className="text-amber-300/80 font-mono">T{r.tier}</span> · {r.leader_name || r.name}
                              </span>
                              <span className="font-mono text-[9px] text-white/40 shrink-0">{r.win_xp} XP</span>
                            </div>
                            {(r.traits || []).length > 0 && (
                              <div className="mt-0.5 space-y-0.5">
                                {r.traits.map((t) => (
                                  <div key={t.name} className="text-[9px] leading-tight flex items-start gap-1" data-testid={`open-delve-${d.id}-room-${i}-trait-${t.name}`}>
                                    <span className="font-mono font-semibold shrink-0" style={traitColor(t)}>✦ {t.name}</span>
                                    {t.desc && <span className="text-white/50">— {t.desc}</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hud-pill rounded-2xl p-5">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 mb-3 flex items-center gap-1.5"><Skull className="h-3.5 w-3.5" /> Recent delves</h3>
            {(history || []).length === 0 ? (
              <p className="text-[12px] text-white/40">No delves yet.</p>
            ) : (
              <div className="space-y-2">
                {history.slice(0, 6).map((h) => (
                  <button
                    key={h.id}
                    onClick={() => navigate(`/cavern/${h.id}`)}
                    title={`${h.card?.title || "Delver"}${h.card?.rarity ? " · " + h.card.rarity : ""}\nvs ${h.foe?.name || "Delve"}`}
                    className="w-full flex items-center gap-2 text-left rounded-lg px-2 py-1.5 hover:bg-white/5"
                  >
                    <div className={`h-8 w-6 rounded overflow-hidden bg-black shrink-0 rarity-${h.card?.rarity}`}>{h.card?.front_image && <img src={h.card.front_image} alt="" className="w-full h-full object-cover" />}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-xs truncate">{h.foe?.name || "Delve"}</div>
                      <div className="font-mono text-[10px] text-white/40">
                        {h.status === "done" ? (h.my_outcome?.outcome === "mint" ? <span className="text-emerald-300">Won · minted</span> : h.my_outcome?.outcome === "forfeit" ? <span className="text-red-400">Lost · forfeited</span> : (h.won ? "Cleared" : "Wiped")) : h.status}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-white/30" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <EditionPicker group={editionPick} selected={selected} onPick={(id) => { setSelected(id); setEditionPick(null); }} onClose={() => setEditionPick(null)} />
    </div>
  );
}
